import React, { useEffect, useRef, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import Grid2 from '@mui/material/Grid2';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { alpha } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import LockIcon from '@mui/icons-material/Lock';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import { CONSTANTS } from 'clink-components';
import i18next from 'v2/helpers/i18n';
import disabledTextFieldSx from './disabledSx';
import { amountInWords } from '../helpers/numberToWords';

const { clinkGreen } = CONSTANTS.colors.general;
export const DEFAULT_CONTRACTOR_ENTITY = 'MCLAREN CONSTRUCTION (LONDON) LIMITED';
const FIELD_ERROR_I18N_KEYS = {
  contractor_entity: 'mclaren-contractor-entity-error',
  commencement_date: 'commencement-date-error',
  authorised_expenditure: 'authorised-expenditure-error',
  authorised_expenditure_words: 'authorised-expenditure-words-error',
  loi_expiry_date: 'loi-expiry-date-error',
  authorised_activities: 'authorised-activities-error',
  authorised_activities_programme: 'authorised-activities-programme-error',
  authorised_expenditure_breakdown: 'authorised-expenditure-breakdown-error',
  contract_route: 'sub-contract-order-document-error',
};

const LOIDetails = ({
  data,
  projectId,
  dispatch,
  actions,
  approvalRequestSent,
  onLoiDetailsChange,
  errors = [],
}) => {
  const loi = data?.loi_details;
  const disabled = approvalRequestSent || data?.status === 'Pending';
  const hasError = (key) => errors.includes(key);
  const [commencementDate, setCommencementDate] = useState(null);
  const [authorisedExpenditure, setAuthorisedExpenditure] = useState('');
  const [expenditureWords, setExpenditureWords] = useState('');
  const [expiryDate, setExpiryDate] = useState(null);
  const [authorisedActivities, setAuthorisedActivities] = useState('');
  const [authorisedActivitiesProgramme, setAuthorisedActivitiesProgramme] = useState('');
  const [breakdownRows, setBreakdownRows] = useState([]);
  const [savedBreakdownSnapshot, setSavedBreakdownSnapshot] = useState([]);
  const [contractRoute, setContractRoute] = useState('');
  const [justification, setJustification] = useState('');
  const [orderTemplates, setOrderTemplates] = useState([]);
  const lastAutoWordsRef = useRef('');
  const nextRowIdRef = useRef(0);
  const ownBreakdownUpdateRef = useRef(false);
  const lastKnownLoiValuesRef = useRef({});
  const applyIfChanged = (key, incomingValue, setter) => {
    if (lastKnownLoiValuesRef.current[key] === incomingValue) return;
    lastKnownLoiValuesRef.current[key] = incomingValue;
    setter(incomingValue);
  };
  const toComparableRow = (row) => ({
    description: (row?.description ?? '').toString().trim(),
    amount:
      row?.amount === '' || row?.amount === null || row?.amount === undefined
        ? 0
        : Number(row.amount),
    notes: (row?.notes ?? '').toString().trim(),
  });

  const isBreakdownRowValid = (row) => (row?.description ?? '').toString().trim() !== '' || (row?.amount ?? '').toString().trim() !== '' || (row?.notes ?? '').toString().trim() !== '';
  useEffect(() => {
    if (!loi) return;

    if (loi.commencement_date) {
      applyIfChanged('commencement_date', loi.commencement_date, setCommencementDate);
    }
    if (loi.authorised_expenditure !== undefined && loi.authorised_expenditure !== null) {
      applyIfChanged('authorised_expenditure', loi.authorised_expenditure, setAuthorisedExpenditure);
    }
    if (loi.loi_expiry_date) {
      applyIfChanged('loi_expiry_date', loi.loi_expiry_date, setExpiryDate);
    }
    if (loi.authorised_activities) {
      applyIfChanged('authorised_activities', loi.authorised_activities, setAuthorisedActivities);
    }
    if (loi.authorised_activities_programme) {
      applyIfChanged(
        'authorised_activities_programme',
        loi.authorised_activities_programme,
        setAuthorisedActivitiesProgramme,
      );
    }
    if (loi.contract_route) setContractRoute(loi.contract_route);
    if (loi.justification) {
      applyIfChanged('justification', loi.justification, setJustification);
    }

    if (Array.isArray(loi.authorised_expenditure_breakdown)) {
      if (ownBreakdownUpdateRef.current) {
        ownBreakdownUpdateRef.current = false;
      } else {
        const loadedRows = loi.authorised_expenditure_breakdown.map((row, index) => ({
          id: row.id ?? index + 1,
          description: row.description ?? '',
          amount: row.amount ?? '',
          notes: row.notes ?? '',
        }));
        setBreakdownRows(loadedRows);
        setSavedBreakdownSnapshot(loadedRows.filter(isBreakdownRowValid).map(toComparableRow));
        nextRowIdRef.current = loadedRows.reduce(
          (max, row) => Math.max(max, Number(row.id) || 0),
          0,
        );
      }
    }
    const computedAutoWords = amountInWords(loi.authorised_expenditure);
    const incomingWords = loi.authorised_expenditure_words ?? computedAutoWords;
    if (lastKnownLoiValuesRef.current.authorised_expenditure_words !== incomingWords) {
      lastKnownLoiValuesRef.current.authorised_expenditure_words = incomingWords;
      lastAutoWordsRef.current = computedAutoWords;
      setExpenditureWords(incomingWords);
    }
  }, [loi]);

  useEffect(() => {
    if (!actions?.fetchTemplates) return undefined;
    let isActive = true;
    Promise.resolve(dispatch(actions.fetchTemplates({ type: 'orders' })))
      .then((result) => {
        if (isActive && Array.isArray(result?.payload)) {
          setOrderTemplates(result.payload);
        }
      })
      .catch(() => {});
    return () => {
      isActive = false;
    };
  }, [actions, dispatch]);

  useEffect(() => {
    const autoWords = amountInWords(authorisedExpenditure);
    const isUntouched =
      expenditureWords === '' || expenditureWords === lastAutoWordsRef.current;

    if (isUntouched) {
      setExpenditureWords(autoWords);
      lastAutoWordsRef.current = autoWords;
    }
  }, [authorisedExpenditure, expenditureWords]);
  const normalizeBreakdownRow = (row) => ({
    description: row?.description ?? '',
    amount:
      row?.amount === '' || row?.amount === null || row?.amount === undefined
        ? 0
        : Number(row.amount),
    notes: row?.notes ?? '',
  });
  const persistBreakdown = (rows) => {
    const tid = data?.tender_recommendation_id;
    if (!projectId || !tid) return;
    ownBreakdownUpdateRef.current = true;
    const dispatched = dispatch(
      actions.updateTenderRecommendationById({
        project_id: projectId,
        tid,
        data: {
          loi_details: { authorised_expenditure_breakdown: rows.map(normalizeBreakdownRow) },
        },
      }),
    );
    Promise.resolve(dispatched)
      .then((action) => {
        if (action && typeof action.type === 'string' && action.type.endsWith('/fulfilled')) {
          setSavedBreakdownSnapshot(rows.filter(isBreakdownRowValid).map(toComparableRow));
        } else if (action && typeof action.type === 'string') {
          ownBreakdownUpdateRef.current = false;
        }
      })
      .catch(() => {
        ownBreakdownUpdateRef.current = false;
      });
  };

  const handleAddBreakdownRow = () => {
    nextRowIdRef.current += 1;
    setBreakdownRows((prev) => [
      ...prev,
      { id: nextRowIdRef.current, description: '', amount: '', notes: '' },
    ]);
  };

  const handleUpdateBreakdownRow = (id, field, value) => {
    setBreakdownRows((prev) =>
      prev.map((row) => (row.id === id ? { ...row, [field]: value } : row)),
    );
  };
  const handleDeleteBreakdownRow = (id) => {
    const remainingRows = breakdownRows.filter((row) => row.id !== id);
    setBreakdownRows(remainingRows);
    persistBreakdown(remainingRows);
  };
  useEffect(() => {
    onLoiDetailsChange?.({
      contractor_entity: loi?.contractor_entity ?? DEFAULT_CONTRACTOR_ENTITY,
      commencement_date: commencementDate || null,
      authorised_expenditure:
        authorisedExpenditure === '' ? null : Number(authorisedExpenditure),
      authorised_expenditure_words: expenditureWords,
      loi_expiry_date: expiryDate || null,
      authorised_activities: authorisedActivities,
      authorised_activities_programme: authorisedActivitiesProgramme,
      authorised_expenditure_breakdown: breakdownRows.map((row) => ({
        id: row.id,
        description: row.description,
        amount: row.amount === '' ? null : Number(row.amount),
        notes: row.notes,
      })),
      contract_route: contractRoute || null,
      justification,
    });
  }, [
    commencementDate,
    authorisedExpenditure,
    expenditureWords,
    expiryDate,
    authorisedActivities,
    authorisedActivitiesProgramme,
    breakdownRows,
    contractRoute,
    justification,
    onLoiDetailsChange,
    loi?.contractor_entity,
  ]);
  const handleFieldBlur = (apiKey, currentValue, originalValue, loiKey = apiKey) => () => {
    const tid = data?.tender_recommendation_id;
    if (projectId && tid && currentValue !== originalValue) {
      lastKnownLoiValuesRef.current[loiKey] = currentValue;
      dispatch(
        actions.updateTenderRecommendationById({
          project_id: projectId,
          tid,
          data: { loi_details: { [apiKey]: currentValue } },
        }),
      );
    }
  };
  // authorised_expenditure_in_words is auto-computed from the amount, so it
  // needs to be saved alongside authorised_expenditure here too - otherwise
  // blurring the amount alone (without also blurring the words field) would
  // silently leave an auto-updated words value unpersisted.
  const handleAuthorisedExpenditureBlur = () => {
    const tid = data?.tender_recommendation_id;
    const currentValue = authorisedExpenditure === '' ? null : Number(authorisedExpenditure);
    const originalValue = loi?.authorised_expenditure ?? null;
    if (projectId && tid && currentValue !== originalValue) {
      const words = amountInWords(authorisedExpenditure);
      lastKnownLoiValuesRef.current.authorised_expenditure = currentValue;
      lastKnownLoiValuesRef.current.authorised_expenditure_words = words;
      dispatch(
        actions.updateTenderRecommendationById({
          project_id: projectId,
          tid,
          data: {
            loi_details: {
              authorised_expenditure: currentValue,
              authorised_expenditure_in_words: words,
            },
          },
        }),
      );
    }
  };

  // The document is stored by name (it is printed as-is in the LOI letter),
  // so a previously saved value that is no longer an available template is
  // still listed to keep it visible.
  const orderTemplateNames = orderTemplates.map((template) => template.name);
  const contractRouteOptions =
    contractRoute && !orderTemplateNames.includes(contractRoute)
      ? [contractRoute, ...orderTemplateNames]
      : orderTemplateNames;
  const handleContractRouteChange = (event) => {
    const selectedValue = event.target.value;
    setContractRoute(selectedValue);
    handleFieldBlur(
      'agreed_subcontract_order_document',
      selectedValue,
      loi?.contract_route ?? '',
      'contract_route',
    )();
  };

  const handleSaveBreakdown = () => {
    persistBreakdown(breakdownRows);
  };
  const validCurrentBreakdownRows = breakdownRows.filter(isBreakdownRowValid).map(toComparableRow);
  const isBreakdownDirty =
    validCurrentBreakdownRows.length > 0 &&
    (validCurrentBreakdownRows.length !== savedBreakdownSnapshot.length ||
      validCurrentBreakdownRows.some(
        (row, index) => JSON.stringify(row) !== JSON.stringify(savedBreakdownSnapshot[index]),
      ));
  return (
    <Grid2 container spacing={0} sx={{ flexDirection: 'column', px: 3, pb: 3 }}>
      <Box sx={{ mb: 2 }}>
        <Alert
          severity="info"
          sx={{
            backgroundColor: alpha(clinkGreen, 0.12),
            '& .MuiAlert-icon': { color: clinkGreen },
          }}
        >
          {i18next.t('loi-details-banner')}
        </Alert>
      </Box>

      {errors.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="error">
            <ul style={{ margin: 0, paddingLeft: '1.2em' }}>
              {errors.map((key) => (
                <li key={key}>{i18next.t(FIELD_ERROR_I18N_KEYS[key])}</li>
              ))}
            </ul>
          </Alert>
        </Box>
      )}

      <Grid2 container spacing={2}>
        <Grid2 size={12}>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            {i18next.t('mclaren-contractor-entity')}
          </Typography>
          <TextField
            fullWidth
            disabled
            variant="outlined"
            size="small"
            value={loi?.contractor_entity ?? DEFAULT_CONTRACTOR_ENTITY}
            error={hasError('contractor_entity')}
            helperText={
              hasError('contractor_entity')
                ? i18next.t(FIELD_ERROR_I18N_KEYS.contractor_entity)
                : i18next.t('mclaren-contractor-entity-helper')
            }
            sx={disabledTextFieldSx}
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <LockIcon fontSize="small" sx={{ color: 'text.disabled' }} />
                  </InputAdornment>
                ),
              },
            }}
          />
        </Grid2>

        <Grid2 size={{ xs: 12, sm: 6 }}>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            {i18next.t('commencement-date')}
          </Typography>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              format="DD/MM/YYYY"
              value={commencementDate ? dayjs(commencementDate) : null}
              onChange={(newValue) =>
                setCommencementDate(
                  newValue ? newValue.format('YYYY-MM-DD') : null
                )
              }
              onAccept={(newValue) => {
                const formattedDate = newValue
                  ? newValue.format('YYYY-MM-DD')
                  : null;

                handleFieldBlur(
                  'authorised_activities_commencement_date',
                  formattedDate,
                  loi?.commencement_date ?? null,
                  'commencement_date',
                )();
              }}
              disabled={disabled}
              slotProps={{
                textField: {
                  size: 'small',
                  fullWidth: true,
                  variant: 'outlined',
                  placeholder: 'dd/mm/yyyy',
                  error: hasError('commencement_date'),
                  helperText: hasError('commencement_date')
                    ? i18next.t(FIELD_ERROR_I18N_KEYS.commencement_date)
                    : i18next.t('commencement-date-helper'),
                  sx: disabledTextFieldSx,
                },
              }}
            />
          </LocalizationProvider>
        </Grid2>

        <Grid2 size={{ xs: 12, sm: 6 }}>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            {i18next.t('authorised-expenditure')}
          </Typography>
          <TextField
            fullWidth
            type="number"
            variant="outlined"
            size="small"
            value={authorisedExpenditure}
            onChange={(event) => setAuthorisedExpenditure(event.target.value)}
            onBlur={handleAuthorisedExpenditureBlur}
            disabled={disabled}
            error={hasError('authorised_expenditure')}
            helperText={
              hasError('authorised_expenditure')
                ? i18next.t(FIELD_ERROR_I18N_KEYS.authorised_expenditure)
                : i18next.t('authorised-expenditure-helper')
            }
            sx={disabledTextFieldSx}
            slotProps={{
              input: {
                startAdornment: <InputAdornment position="start">£</InputAdornment>,
                inputProps: { min: 0 },
              },
            }}
          />
        </Grid2>

        <Grid2 size={12}>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            {i18next.t('authorised-expenditure-words')}
          </Typography>
          <TextField
            fullWidth
            variant="outlined"
            size="small"
            disabled={disabled}
            value={expenditureWords}
            onChange={(event) => setExpenditureWords(event.target.value)}
            onBlur={handleFieldBlur(
              'authorised_expenditure_in_words',
              expenditureWords,
              loi?.authorised_expenditure_words ?? '',
              'authorised_expenditure_words',
            )}
            placeholder={i18next.t('authorised-expenditure-words-placeholder')}
            error={hasError('authorised_expenditure_words')}
            helperText={
              hasError('authorised_expenditure_words')
                ? i18next.t(FIELD_ERROR_I18N_KEYS.authorised_expenditure_words)
                : i18next.t('authorised-expenditure-words-helper')
            }
            sx={disabledTextFieldSx}
          />
        </Grid2>

        <Grid2 size={{ xs: 12, sm: 6 }}>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            {i18next.t('loi-expiry-date')}
          </Typography>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              format="DD/MM/YYYY"
              value={expiryDate ? dayjs(expiryDate) : null}
              minDate={dayjs()}
              onChange={(newValue) => {
                setExpiryDate(newValue ? newValue.format('YYYY-MM-DD') : null);
              }}
              onAccept={(newValue) => {
                const formattedDate = newValue
                  ? newValue.format('YYYY-MM-DD')
                  : null;

                handleFieldBlur(
                  'expiry_date',
                  formattedDate,
                  loi?.loi_expiry_date ?? null,
                  'loi_expiry_date',
                )();
              }}
              disabled={disabled}
              slotProps={{
                textField: {
                  size: 'small',
                  fullWidth: true,
                  variant: 'outlined',
                  placeholder: 'dd/mm/yyyy',
                  error: hasError('loi_expiry_date'),
                  helperText: hasError('loi_expiry_date')
                    ? i18next.t(FIELD_ERROR_I18N_KEYS.loi_expiry_date)
                    : i18next.t('loi-expiry-date-helper'),
                  sx: disabledTextFieldSx,
                },
              }}
            />
          </LocalizationProvider>
        </Grid2>

        <Grid2 size={12}>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            {i18next.t('authorised-activities')}
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={4}
            variant="outlined"
            size="small"
            value={authorisedActivities}
            onChange={(event) => setAuthorisedActivities(event.target.value)}
            onBlur={handleFieldBlur(
              'authorised_activities',
              authorisedActivities,
              loi?.authorised_activities ?? '',
            )}
            disabled={disabled}
            placeholder={i18next.t('authorised-activities-placeholder')}
            error={hasError('authorised_activities')}
            helperText={
              hasError('authorised_activities')
                ? i18next.t(FIELD_ERROR_I18N_KEYS.authorised_activities)
                : i18next.t('authorised-activities-helper')
            }
            sx={disabledTextFieldSx}
            slotProps={{ input: { inputProps: { maxLength: 250 } } }}
          />
        </Grid2>

        <Grid2 size={12}>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            {i18next.t('authorised-activities-programme')}
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={4}
            variant="outlined"
            size="small"
            value={authorisedActivitiesProgramme}
            onChange={(event) => setAuthorisedActivitiesProgramme(event.target.value)}
            onBlur={handleFieldBlur(
              'authorised_activities_programme',
              authorisedActivitiesProgramme,
              loi?.authorised_activities_programme ?? '',
            )}
            disabled={disabled}
            placeholder={i18next.t('authorised-activities-programme-placeholder')}
            error={hasError('authorised_activities_programme')}
            helperText={
              hasError('authorised_activities_programme')
                ? i18next.t(FIELD_ERROR_I18N_KEYS.authorised_activities_programme)
                : i18next.t('authorised-activities-programme-helper')
            }
            sx={disabledTextFieldSx}
            slotProps={{ input: { inputProps: { maxLength: 250 } } }}
          />
        </Grid2>

        <Grid2 size={12}>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            {i18next.t('authorised-expenditure-breakdown')}
          </Typography>
          <TableContainer
            component={Paper}
            variant="outlined"
            sx={
              hasError('authorised_expenditure_breakdown')
                ? { borderColor: 'error.main' }
                : undefined
            }
          >
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: 'grey.100' }}>
                  <TableCell sx={{ fontWeight: 'bold', width: '45%' }}>
                    {i18next.t('description')}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', width: '20%' }}>
                    {i18next.t('amount-gbp')}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', width: '25%' }}>
                    {i18next.t('notes')}
                  </TableCell>
                  <TableCell sx={{ width: '10%' }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {breakdownRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                      {i18next.t('no-breakdown-rows-yet')}
                    </TableCell>
                  </TableRow>
                ) : (
                  breakdownRows.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell>
                        <TextField
                          fullWidth
                          size="small"
                          variant="outlined"
                          value={row.description}
                          placeholder={i18next.t('description')}
                          onChange={(event) =>
                            handleUpdateBreakdownRow(row.id, 'description', event.target.value)
                          }
                          disabled={disabled}
                          sx={disabledTextFieldSx}
                          slotProps={{ input: { inputProps: { maxLength: 250 } } }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          fullWidth
                          type="number"
                          size="small"
                          variant="outlined"
                          value={row.amount}
                          onChange={(event) =>
                            handleUpdateBreakdownRow(row.id, 'amount', event.target.value)
                          }
                          disabled={disabled}
                          sx={disabledTextFieldSx}
                          slotProps={{
                            input: {
                              startAdornment: (
                                <InputAdornment position="start">£</InputAdornment>
                              ),
                              inputProps: { min: 0 },
                            },
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          fullWidth
                          size="small"
                          variant="outlined"
                          value={row.notes}
                          placeholder={i18next.t('notes')}
                          onChange={(event) =>
                            handleUpdateBreakdownRow(row.id, 'notes', event.target.value)
                          }
                          disabled={disabled}
                          sx={disabledTextFieldSx}
                          slotProps={{ input: { inputProps: { maxLength: 250 } } }}
                        />
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          color="error"
                          disabled={disabled}
                          aria-label={i18next.t('delete-row')}
                          onClick={() => handleDeleteBreakdownRow(row.id)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <Box sx={{ display: 'flex', gap: 1.5, mt: 1.5 }}>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleAddBreakdownRow}
              disabled={disabled}
              sx={{
                color: clinkGreen,
                borderColor: clinkGreen,
                '&:hover': {
                  borderColor: clinkGreen,
                  backgroundColor: alpha(clinkGreen, 0.08),
                },
              }}
            >
              {i18next.t('add-row')}
            </Button>
            <Button
              variant="contained"
              startIcon={<SaveOutlinedIcon fontSize="small" />}
              onClick={handleSaveBreakdown}
              disabled={disabled || !isBreakdownDirty}
              sx={{
                backgroundColor: clinkGreen,
                '&:hover': { backgroundColor: alpha(clinkGreen, 0.85) },
              }}
            >
              {i18next.t('save-schedule-c')}
            </Button>
          </Box>
          <FormHelperText error={hasError('authorised_expenditure_breakdown')}>
            {hasError('authorised_expenditure_breakdown')
              ? i18next.t(FIELD_ERROR_I18N_KEYS.authorised_expenditure_breakdown)
              : i18next.t('authorised-expenditure-breakdown-helper')}
          </FormHelperText>
        </Grid2>

        <Grid2 size={12}>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            {i18next.t('sub-contract-order-document')}
          </Typography>
          <FormControl fullWidth error={hasError('contract_route')}>
            <Select
              data-testid="loi-contract-route-select"
              value={contractRoute}
              onChange={handleContractRouteChange}
              displayEmpty
              size="small"
              variant="outlined"
              disabled={disabled}
              sx={disabledTextFieldSx}
            >
              <MenuItem value="">
                {i18next.t('select-sub-contract-order-document')}
              </MenuItem>
              {contractRouteOptions.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>
              {hasError('contract_route')
                ? i18next.t(FIELD_ERROR_I18N_KEYS.contract_route)
                : i18next.t('sub-contract-order-document-helper')}
            </FormHelperText>
          </FormControl>
        </Grid2>

        <Grid2 size={12}>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            {i18next.t('loi-reason-justification')}
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={4}
            variant="outlined"
            size="small"
            value={justification}
            onChange={(event) => setJustification(event.target.value)}
            onBlur={handleFieldBlur(
              'reason',
              justification,
              loi?.justification ?? '',
              'justification',
            )}
            disabled={disabled}
            placeholder={i18next.t('loi-reason-justification-placeholder')}
            helperText={i18next.t('loi-reason-justification-helper')}
            sx={disabledTextFieldSx}
            slotProps={{ input: { inputProps: { maxLength: 250 } } }}
          />
        </Grid2>
      </Grid2>
    </Grid2>
  );
};

export default LOIDetails;
